// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/relatedClaimsReveal.test.tsx
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";

import { type AskV2RailSectionId } from "./types";

/** Show one answer's Related claims in the Context pane. */
export type RevealRelatedClaims = (messageId: string) => void;

const RelatedClaimsRevealContext = createContext<RevealRelatedClaims | null>(
  null,
);

/**
 * Lets the thread's "+N related" label open the Context pane's Related claims.
 *
 * Outside a page with a Context pane (the legacy chat, the style guide, a unit
 * render) there is nothing to reveal, so the hook returns null and the label
 * stays plain text. A page passes `reveal={null}` while its pane is not shown
 * (a review open, a tab with its own thread) for the same reason.
 */
export function RelatedClaimsRevealProvider({
  reveal,
  children,
}: {
  reveal: RevealRelatedClaims | null;
  children: ReactNode;
}) {
  return (
    <RelatedClaimsRevealContext.Provider value={reveal}>
      {children}
    </RelatedClaimsRevealContext.Provider>
  );
}

export function useRevealRelatedClaims(): RevealRelatedClaims | null {
  return useContext(RelatedClaimsRevealContext);
}

/**
 * Page state behind the reveal: opens the rail and its Context section, points
 * the pane at the clicked answer when the page tracks one, and bumps `signal`
 * so the Related claims toggle opens even when the reader had closed it.
 */
export function useRelatedClaimsRevealState({
  setRailOpen,
  setSectionOpen,
  focusMessage,
}: {
  setRailOpen: (open: boolean) => void;
  setSectionOpen: Dispatch<SetStateAction<Record<AskV2RailSectionId, boolean>>>;
  focusMessage?: (messageId: string) => void;
}): { reveal: RevealRelatedClaims; signal: number } {
  const [signal, setSignal] = useState(0);
  const reveal = useCallback<RevealRelatedClaims>(
    (messageId) => {
      focusMessage?.(messageId);
      setRailOpen(true);
      setSectionOpen((current) =>
        current.context ? current : { ...current, context: true },
      );
      setSignal((current) => current + 1);
    },
    [focusMessage, setRailOpen, setSectionOpen],
  );
  return { reveal, signal };
}
